/**
 * useVideoModels — fetches available video models from the backend,
 * splitting them into native (Dreamwrite-hosted), gateway (user's key),
 * and BYOM (user-registered) sections.
 *
 * Re-fetches when the gateway config version changes (bumped by the
 * GatewaySettingsModal after save/delete).
 *
 * Backed by React Query so the request is fetched ONCE and shared across every
 * consumer, not re-fetched per node. This matters because `VideoGeneratorNode`
 * calls this hook unconditionally at its top level: the previous
 * `useEffect` + `fetch` implementation had no in-flight dedupe and no freshness
 * window, so a board with N video nodes issued N concurrent `GET /video-models`
 * requests that competed with poster/thumbnail loads for the browser's handful
 * of per-origin connections. It compounded with `renderOnlyVisibleElements`
 * (default-on): ReactFlow unmounts/remounts nodes as the canvas pans, so every
 * pan re-fired the whole burst, and every response then `setState`d into N
 * copies of a very large component.
 *
 * The module-level `cachedResponse` that used to live here only seeded initial
 * state — the effect still fetched unconditionally on every mount — so it did
 * not dedupe anything. React Query's cache replaces it outright.
 *
 * This mirrors the other catalog hooks in the console.
 */

import { useCallback, useSyncExternalStore } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getConfiguredApiBase,
  getVideoModels,
  type VideoModelsBySourceResponse,
  type ByomModelInfo,
} from "../api/client";
import { useAuthOptional } from "../contexts/authContextRaw";
import { createNadDebug } from "../utils/debugLog";

const log = createNadDebug("dw:video-model-catalog:query");

/**
 * A successfully fetched model list stays fresh for the workspace session.
 *
 * The set of available models only changes when the user edits a gateway/BYOM
 * config, and that path notifies us explicitly via
 * `notifyGatewayConfigChanged()`. There is no time-based refresh: capability
 * metadata is stable until the user mutates their BYOM configuration.
 */
export const VIDEO_MODELS_CACHE_TTL_MS = Infinity;

/** Base React Query key; the gateway-config version is appended per query. */
export const VIDEO_MODELS_KEY = "video-models";

export function getVideoModelsCacheScope(
  apiBase: string,
  userId: string | null | undefined,
): string {
  return `${apiBase}::${userId ?? "anonymous"}`;
}

interface UseVideoModelsResult {
  native: string[];
  gateway: string[];
  byom: ByomModelInfo[];
  defaultModel: string | null;
  isLoading: boolean;
  /** True only after this user+environment catalog loaded successfully. */
  isReady: boolean;
  error: string | null;
  /** Call to re-fetch models (e.g. after gateway config change) */
  refetch: () => void;
}

const EMPTY_RESPONSE: VideoModelsBySourceResponse = {
  native: [],
  gateway: [],
  byom: [],
  default_model: null,
};

/**
 * Module-level version counter bumped by notifyGatewayConfigChanged().
 *
 * Read through `useSyncExternalStore` rather than a per-instance
 * `useState` + `useEffect` listener so that every mounted hook observes the
 * SAME value in the same render pass. With per-instance state the instances
 * transiently disagreed, which under a version-keyed query would mean several
 * distinct keys in flight at once — exactly the fan-out this change removes.
 */
let gatewayConfigVersion = 0;
const listeners = new Set<() => void>();

/**
 * Subscribe to gateway (BYOM) config changes. Also used by `useProjectConfig`:
 * `effective_default_video_model` depends on the user's default BYOM model.
 */
export function subscribeToGatewayConfig(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}

function getGatewayConfigVersion(): number {
  return gatewayConfigVersion;
}

/**
 * Call after saving or deleting a gateway config so all mounted
 * useVideoModels hooks re-fetch the model list.
 */
export function notifyGatewayConfigChanged(): void {
  gatewayConfigVersion++;
  for (const listener of listeners) {
    listener();
  }
}

/** @internal — reset module-level state for tests. */
export function _resetCacheForTests(): void {
  gatewayConfigVersion = 0;
  listeners.clear();
}

export function useVideoModels(): UseVideoModelsResult {
  const version = useSyncExternalStore(
    subscribeToGatewayConfig,
    getGatewayConfigVersion,
    getGatewayConfigVersion,
  );
  const queryClient = useQueryClient();
  const auth = useAuthOptional();
  const cacheScope = getVideoModelsCacheScope(getConfiguredApiBase(), auth?.user?.id);

  const query = useQuery({
    queryKey: [VIDEO_MODELS_KEY, cacheScope, version],
    queryFn: async () => {
      log({
        event: "fetch:start",
        trace_id: "video-model-catalog:fetch",
        cache_scope: cacheScope,
        version,
      });
      try {
        const result = await getVideoModels();
        log({
          event: "fetch:ok",
          trace_id: "video-model-catalog:fetch",
          cache_scope: cacheScope,
          native_count: result.native.length,
          byom_count: result.byom?.length ?? 0,
          default_model: result.default_model,
        });
        return result;
      } catch (err) {
        log({
          event: "fetch:error",
          trace_id: "video-model-catalog:fetch",
          cache_scope: cacheScope,
          error: err instanceof Error ? err.message : String(err),
        });
        throw err;
      }
    },
    staleTime: VIDEO_MODELS_CACHE_TTL_MS,
    // Bumping the version mints a fresh key with no cached entry. Carrying the
    // previous version's data forward as placeholder preserves the prior
    // behavior of keeping the last response on screen while the re-fetch runs,
    // instead of flashing empty BYOM chips right after a gateway config save.
    placeholderData: (
      previous: VideoModelsBySourceResponse | undefined,
      previousQuery,
    ) => previousQuery?.queryKey[1] === cacheScope ? previous : undefined,
    // The prior implementation was a single bare fetch with a `.catch`. Keeping
    // retries off preserves that: a /video-models outage surfaces immediately
    // rather than being retried behind the caller's back.
    retry: false,
  });

  const refetch = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: [VIDEO_MODELS_KEY, cacheScope] });
  }, [cacheScope, queryClient]);

  const data = query.data ?? EMPTY_RESPONSE;

  return {
    native: data.native,
    gateway: data.gateway,
    byom: data.byom ?? [],
    defaultModel: data.default_model ?? null,
    // `isPending` is true only while there is nothing to show — matching the
    // old "spinner only on a cold cache" rule, since placeholder data from a
    // previous version counts as something to show.
    isLoading: query.isPending,
    // A version bump can retain the prior catalog as placeholder data while
    // the replacement is in flight. That data is renderable but not safe for
    // normalization because the user just changed its capability contract.
    isReady: query.isSuccess && !query.isFetching,
    error: query.error
      ? query.error instanceof Error
        ? query.error.message
        : String(query.error)
      : null,
    refetch,
  };
}
